import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import SaveIcon from "@mui/icons-material/Save";
import { Typography } from "@mui/material";
import { toast } from "../../../common_components/Toast/ToastUtils";
import { fetchClientConnections, updateAffiliateAccountStatus, selectConnectionsByClient, selectOldConnectionsByClient, selectConnectionsLoading, selectAffiliateSaving, selectAffiliateStatusUpdating, selectConnectionDeleting, deleteConnectionById, } from "../../../redux/clientDataSlice";
// Components
import ConfirmActionModal from "../../../common_components/ConfirmActionModal";
import CommonLoader from "../../../common_components/CommonLoader";
import AppButton from "../../../common_components/AppButton";
import "../../../styles/AffiliateDrawer.css";
import CommonDrawerLayout from "../../../common_components/CommonDrawerLayout";
import ImagePreviewModal from "./components/ImagePreviewModal";
import { NetworkForm } from "./components/AffiliateUIComponents";
import { NETWORK_CONFIGS, createDefaultAccount, deepClone, getNetworkInstructions } from "./components/AffiliateNetworkConfig";
/* ================= CUSTOM HOOKS ================= */
const useAccounts = (networkId) => {
    const [accounts, setAccounts] = useState([]);
    const [originalAccounts, setOriginalAccounts] = useState([]);
    const initialize = useCallback((data) => {
        setAccounts(data);
        setOriginalAccounts(deepClone(data));
    }, []);
    const reset = useCallback(() => {
        if (networkId) {
            setAccounts([createDefaultAccount(networkId)]);
            setOriginalAccounts([]);
        }
    }, [networkId]);
    const update = useCallback((index, field, value) => {
        setAccounts((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    }, []);
    const add = useCallback(() => {
        if (networkId)
            setAccounts((prev) => [...prev, createDefaultAccount(networkId)]);
    }, [networkId]);
    const remove = useCallback((index) => {
        if (networkId) {
            setAccounts((prev) => prev.length > 1
                ? prev.filter((_, i) => i !== index)
                : [createDefaultAccount(networkId)]);
        }
    }, [networkId]);
    const updateStatus = useCallback((accountId, status) => {
        // Backend ties api_enabled to status (active -> true, inactive -> false); mirror it here so the
        // API Enabled toggle updates immediately. Update the baseline too so it isn't seen as an edit.
        const apply = (acc) => acc._id === accountId ? { ...acc, status, api_enabled: status === "active" } : acc;
        setAccounts((prev) => prev.map(apply));
        setOriginalAccounts((prev) => prev.map(apply));
    }, []);
    const getChangedAccounts = useCallback(() => {
        if (!networkId)
            return [];
        const config = NETWORK_CONFIGS[networkId];
        const compareFields = config.fields.map((f) => f.name);
        return accounts.filter((current) => {
            if (!current._id)
                return compareFields.some((field) => current[field]?.trim?.());
            const original = originalAccounts.find((acc) => acc._id === current._id);
            if (!original)
                return true;
            // Toggling API Enabled alone also counts as a change so Save persists it.
            if ((current.api_enabled !== false) !== (original.api_enabled !== false))
                return true;
            return compareFields.some((field) => current[field] !== original[field]);
        });
    }, [accounts, originalAccounts, networkId]);
    return { accounts, initialize, reset, update, add, remove, updateStatus, getChangedAccounts, setAccounts };
};
const AffiliateNetworkAccount = ({ open, onClose, network, clientId, clientName, refetchClientList }) => {
    const dispatch = useDispatch();
    const networkId = network?.id;
    const connections = useSelector((state) => selectConnectionsByClient(clientId, network?.id)(state));
    // Inactive connections (returned separately as old_entries) so they also show in the form.
    const oldConnections = useSelector((state) => selectOldConnectionsByClient(clientId, network?.id)(state));
    const isLoading = useSelector(selectConnectionsLoading);
    const isSaving = useSelector(selectAffiliateSaving);
    const isDeleting = useSelector(selectConnectionDeleting);
    const isStatusUpdating = useSelector(selectAffiliateStatusUpdating);
    const [changingStatusAccountId, setChangingStatusAccountId] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [accountToDelete, setAccountToDelete] = useState(null);
    const [activeImage, setActiveImage] = useState(null);
    const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
    const [statusChangeData, setStatusChangeData] = useState(null);
    const { accounts, initialize, reset, update, add, remove, updateStatus, getChangedAccounts, setAccounts } = useAccounts(networkId);
    const handleOpenImage = useCallback((key) => setActiveImage(key), []);
    const handleCloseImage = useCallback(() => setActiveImage(null), []);
    const instructionsData = useMemo(() => getNetworkInstructions(handleOpenImage), [handleOpenImage]);
    // Load data on open
    useEffect(() => {
        if (open && clientId && networkId) {
            dispatch(fetchClientConnections({ clientId, network: networkId }));
        }
    }, [dispatch, open, clientId, networkId]);
    // Process connections
    useEffect(() => {
        if (!open || !networkId)
            return;
        // Show active connections and inactive ones (old_entries) together — inactive
        // accounts render with their "inactive" status just like the list-type networks.
        const allConnections = [...connections, ...oldConnections];
        if (allConnections.length > 0) {
            const config = NETWORK_CONFIGS[networkId];
            // Carry api_enabled from each connection so the toggle reflects saved state (default on).
            initialize(allConnections.map((c) => ({
                ...config.mapFromConnection(c),
                api_enabled: c.api_enabled !== false,
            })));
        }
        else {
            reset();
        }
    }, [connections, oldConnections, open, networkId, initialize, reset]);
    // Validation
    const isSaveDisabled = useMemo(() => {
        if (isSaving || isLoading || !networkId)
            return true;
        const config = NETWORK_CONFIGS[networkId];
        const requiredFields = config.fields.filter((f) => f.required).map((f) => f.name);
        const checkboxFields = config.fields.filter((f) => f.type === "checkbox-group").map((f) => f.name);
        return accounts.some((acc) => requiredFields.some((field) => !acc[field]?.trim?.()) ||
            checkboxFields.some((field) => !acc[field]?.length) // at least one box must stay ticked
        );
    }, [isSaving, isLoading, networkId, accounts]);
    // Handle status change - shows confirmation modal
    const handleStatusChange = useCallback((account, newStatus) => {
        if (!account._id || !networkId)
            return;
        setStatusChangeData({ account, newStatus, networkType: networkId });
        setStatusChangeModalOpen(true);
    }, [networkId]);
    // Confirm status change - calls single API
    const handleStatusChangeConfirm = useCallback(async () => {
        if (!statusChangeData || !networkId)
            return;
        const { account, newStatus } = statusChangeData;
        setChangingStatusAccountId(account._id);
        setStatusChangeModalOpen(false);
        try {
            await dispatch(updateAffiliateAccountStatus({
                clientId,
                networkType: networkId,
                accountId: account._id,
                status: newStatus,
                accountName: account.accountName,
            })).unwrap();
            updateStatus(account._id, newStatus);
            refetchClientList();
        }
        catch (error) {
            console.error("Failed to update status:", error);
        }
        finally {
            setChangingStatusAccountId(null);
            setStatusChangeData(null);
        }
    }, [dispatch, clientId, networkId, statusChangeData, updateStatus, refetchClientList]);
    const handleRemove = useCallback((index) => {
        const account = accounts[index];
        if (account._id && networkId) {
            setAccountToDelete({ _id: account._id, accountName: account.accountName, networkType: networkId, index });
            setDeleteModalOpen(true);
        }
        else {
            remove(index);
        }
    }, [accounts, networkId, remove]);
    const handleDeleteConfirm = useCallback(async () => {
        if (!accountToDelete || !networkId)
            return;
        const { networkType, index, _id, accountName } = accountToDelete;
        try {
            await dispatch(deleteConnectionById({
                connectionId: _id,
            })).unwrap();
            setAccounts((prev) => {
                const updated = prev.filter((_, i) => i !== index);
                return updated.length > 0 ? updated : [createDefaultAccount(networkType)];
            });
            refetchClientList?.();
        }
        catch (error) {
            console.error("Delete failed:", error);
        }
        finally {
            setDeleteModalOpen(false);
            setAccountToDelete(null);
        }
    }, [dispatch, accountToDelete, networkId, setAccounts, refetchClientList]);
    const handleSave = useCallback(async () => {
        if (!networkId)
            return;
        const config = NETWORK_CONFIGS[networkId];
        const changedAccounts = getChangedAccounts();
        if (changedAccounts.length === 0) {
            onClose();
            return;
        }
        let success = true;
        // If multiple accounts are being saved, suppress individual toasts
        const shouldSuppressIndividualToasts = changedAccounts.length > 1;
        for (const account of changedAccounts) {
            try {
                await dispatch(config.connectAction({
                    clientId,
                    payload: { ...config.mapToPayload(account), api_enabled: account.api_enabled !== false },
                    suppressToast: shouldSuppressIndividualToasts
                })).unwrap();
            }
            catch {
                success = false;
            }
        }
        if (success) {
            if (shouldSuppressIndividualToasts) {
                toast.success(`${network?.name} accounts saved successfully!`);
            }
            setTimeout(onClose, 1500);
            refetchClientList?.();
        }
    }, [dispatch, clientId, networkId, getChangedAccounts, onClose, refetchClientList, network?.name]);
    if (!open)
        return null;
    const networkInstructions = networkId ? instructionsData[networkId] : null;
    const headerActions = (_jsx(AppButton, { variant: "contained", onClick: handleSave, startIcon: _jsx(SaveIcon, {}), disabled: isSaveDisabled, loading: isSaving, sx: { backgroundColor: "#7b1fa2", "&:hover": { backgroundColor: "#6a1b9a" }, gap: isSaving ? 1 : 0 }, children: "Save" }));
    return (_jsxs(_Fragment, { children: [_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: `${network?.name} | ${clientName || "Client"}`, headerActions: headerActions, isLoading: isLoading, width: "md", className: "affiliate-drawer", children: _jsx("div", { className: "affiliate-content-section", children: _jsx("div", { className: "affiliate-form-container", children: isLoading ? (_jsx("div", { className: "loading-spinners", children: _jsx(CommonLoader, { className: "table-loader" }) })) : networkId && networkInstructions ? (_jsx(NetworkForm, { networkId: networkId, accounts: accounts, onUpdate: update, onAdd: add, onRemove: handleRemove, onStatusChange: handleStatusChange, changingStatusAccountId: changingStatusAccountId, instructions: networkInstructions.instructions, notes: networkInstructions.notes })) : (_jsx(Typography, { variant: "body2", color: "textSecondary", children: "Network configuration not available." })) }) }) }), _jsx(ConfirmActionModal, { open: deleteModalOpen, title: `Delete ${accountToDelete?.networkType?.toUpperCase() || ""} Account`, confirmText: "Delete", cancelText: "Cancel", isDeleteIcon: true, loading: isDeleting, isConfirm: false, description: `Are you sure you want to delete "${accountToDelete?.accountName}"? This action leads to remove all its records & cannot be undone.`, onClose: () => { setDeleteModalOpen(false); setAccountToDelete(null); }, onConfirm: handleDeleteConfirm }), _jsx(ConfirmActionModal, { open: statusChangeModalOpen, title: statusChangeData?.newStatus === "active" ? "Activate Account" : "Deactivate Account", confirmText: statusChangeData?.newStatus === "active" ? "Activate" : "Deactivate", cancelText: "Cancel", isDeleteIcon: statusChangeData?.newStatus === "inactive", isConfirm: statusChangeData?.newStatus === "active", loading: isStatusUpdating, description: statusChangeData?.newStatus === "active"
                    ? `Are you sure you want to activate "${statusChangeData?.account.accountName}"? This will enable data syncing.`
                    : `Are you sure you want to deactivate "${statusChangeData?.account.accountName}"? Data syncing will be paused.`, onClose: () => { setStatusChangeModalOpen(false); setStatusChangeData(null); }, onConfirm: handleStatusChangeConfirm }), _jsx(ImagePreviewModal, { activeImage: activeImage, onClose: handleCloseImage })] }));
};
export default AffiliateNetworkAccount;
